import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, LayoutGrid, Megaphone, ShieldAlert } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useDebouncedSearch } from '@/client/hooks/useDebouncedSearch';
import { usePromotedListingsQuery } from '@/client/api/promotion-management/promoted-listing.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { ListingsToolbar } from './PromotedListings/ListingsToolbar';
import { ListingsTable } from './PromotedListings/ListingsTable';
import {
  DEFAULT_FILTERS,
  toListingParams,
  type ListingFilters,
} from './PromotedListings/filters';

const PAGE_SIZE = 12;

const SummaryTile: React.FC<{ icon: React.ElementType; label: string; value: string; tone: string }> = ({
  icon: Icon,
  label,
  value,
  tone,
}) => (
  <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-lg font-semibold text-gray-900">{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    </div>
  </div>
);

/**
 * PROMOTED LISTINGS — list view.
 *
 * Search, risk/type/activity filters, sort and paging are all server-side via
 * `/admin/promotions/listings`; picking a row opens its analytics + financials.
 */
const PromotedListingsPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Promotions Management', href: '#' },
    { label: 'Promoted Listings' },
  ]);

  const navigate = useNavigate();
  const [filters, setFilters] = useState<ListingFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const search = useDebouncedSearch('', { delay: 400 });

  const effective = useMemo<ListingFilters>(
    () => ({ ...filters, query: search.debounced }),
    [filters, search.debounced],
  );

  // Any filter change invalidates the current page offset.
  useEffect(() => {
    setPage(1);
  }, [effective.query, effective.risk, effective.type, effective.activity, effective.sort]);

  const params = useMemo(() => toListingParams(effective, page, PAGE_SIZE), [effective, page]);
  const { data, isPending, isFetching, isError, error, refetch } = usePromotedListingsQuery(params);

  // Counts for the tiles: the endpoint reports `total` per filter set, so a
  // 1-row probe per band is the cheapest honest way to get them.
  const atRisk = usePromotedListingsQuery({ ...params, page: 1, page_size: 1, risk: ['high'] });
  const active = usePromotedListingsQuery({ ...params, page: 1, page_size: 1, activity: 'active' });

  // The API has no "list the types" endpoint, so the dropdown learns them from
  // the rows it has seen; the current selection is always kept selectable.
  const [types, setTypes] = useState<string[]>([]);
  useEffect(() => {
    if (!data?.data.length) return;
    setTypes((prev) => {
      const next = new Set(prev);
      data.data.forEach((row) => row.listing.type_name && next.add(row.listing.type_name));
      return [...next].sort();
    });
  }, [data]);
  const typeOptions = useMemo(
    () => (filters.type !== 'all' && !types.includes(filters.type) ? [...types, filters.type].sort() : types),
    [types, filters.type],
  );

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const errorMessage = isError ? getApiErrorMessage(error, 'Failed to fetch promoted listings') : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-8xl space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Promoted Listings</h1>
            <p className="mt-1 text-sm text-gray-500">
              Filter promoted listings by risk, type, and activity — open one to see its analytics and financial records.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SummaryTile
            icon={LayoutGrid}
            label="Promoted listings"
            value={total.toLocaleString()}
            tone="bg-indigo-50 text-indigo-600"
          />
          <SummaryTile
            icon={ShieldAlert}
            label="High risk (under-delivering)"
            value={(atRisk.data?.total ?? 0).toLocaleString()}
            tone="bg-rose-50 text-rose-600"
          />
          <SummaryTile
            icon={Megaphone}
            label="With an active promotion"
            value={(active.data?.total ?? 0).toLocaleString()}
            tone="bg-emerald-50 text-emerald-600"
          />
        </div>

        {errorMessage && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{errorMessage}</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <ListingsToolbar
          filters={effective}
          onChange={(next) => setFilters({ ...next, query: filters.query })}
          search={search.value}
          onSearch={search.setValue}
          types={typeOptions}
          resultCount={total}
          isFetching={isFetching || search.isDebouncing}
          onReset={() => {
            setFilters(DEFAULT_FILTERS);
            search.reset();
          }}
        />

        <ListingsTable
          rows={data?.data ?? []}
          page={page}
          pageCount={pageCount}
          pageSize={PAGE_SIZE}
          isLoading={isPending}
          isFetching={isFetching}
          onPage={setPage}
          onOpen={(eid) => navigate(`/admin/promotions/${eid}`)}
        />
      </div>
    </div>
  );
};

export default PromotedListingsPage;
